import React from 'react';
import { useAsync } from 'react-use';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { X } from '@lifeomic/chromicons';
import { useIntl } from 'react-intl';
import { getNameFromFHIRRecord } from '@lifeomic/phc-web-toolkit/src/util/fhir';

import { POPULATION_AGENT_USERNAME } from '../constants';
import {
  ConversationEntry,
  FhirSubjectSearch,
  resolvePatientIdFromConversation,
} from './chatDockUtils';
import { chatDockMessages } from './chatDockMessages';

type ChatDockRowClasses = {
  conversationItem: string;
  conversationItemDisabled: string;
  conversationMeta: string;
  conversationMetaActions: string;
  conversationRemove: string;
};

const usePatientName = ({
  resolvedPatientId,
  maskedMode,
  fhirSubjectSearch,
  activeProjectId,
  activeProjectCohortId,
}: {
  resolvedPatientId?: string;
  maskedMode: boolean;
  fhirSubjectSearch: FhirSubjectSearch;
  activeProjectId: string;
  activeProjectCohortId?: string;
}) => {
  const fhirSubjectSearchRef = React.useRef(fhirSubjectSearch);
  React.useEffect(() => {
    fhirSubjectSearchRef.current = fhirSubjectSearch;
  }, [fhirSubjectSearch]);

  const { value: patientName } = useAsync(async () => {
    if (!resolvedPatientId || maskedMode || !activeProjectId) return undefined;
    const res = await fhirSubjectSearchRef.current({
      projectId: activeProjectId,
      cohort: activeProjectCohortId || undefined,
      body: {
        _id: resolvedPatientId,
      },
    });
    const patient = res?.data?.entry?.[0]?.resource;
    if (!patient?.name?.length) return undefined;
    const name = getNameFromFHIRRecord(patient.name);
    const display = `${name.given} ${name.family}`.trim();
    return display || undefined;
  }, [resolvedPatientId, maskedMode, activeProjectId, activeProjectCohortId]);

  return patientName;
};

export const ChatDockRow = ({
  conversation,
  openConversation,
  handleHideConversation,
  renderConversationPreview,
  maskedMode,
  fhirSubjectSearch,
  activeProjectId,
  activeProjectCohortId,
  classes,
}: {
  conversation: ConversationEntry;
  openConversation: (entry: {
    conversationId?: string;
    patientId?: string;
    title?: string;
    userIds?: string[];
  }) => void;
  handleHideConversation: (conversation: ConversationEntry) => void;
  renderConversationPreview: (timestamp?: string) => string | null;
  maskedMode: boolean;
  fhirSubjectSearch: FhirSubjectSearch;
  activeProjectId: string;
  activeProjectCohortId?: string;
  classes: ChatDockRowClasses;
}) => {
  const { formatMessage } = useIntl();
  const resolvedPatientId = resolvePatientIdFromConversation(conversation);
  const isDisabled = !resolvedPatientId && !conversation.userIds?.length;
  const isPopulationChat = conversation.userIds?.includes(
    POPULATION_AGENT_USERNAME,
  );
  const patientName = usePatientName({
    resolvedPatientId,
    maskedMode,
    fhirSubjectSearch,
    activeProjectId,
    activeProjectCohortId,
  });
  const displayTitle =
    patientName ||
    conversation.title ||
    (isPopulationChat
      ? formatMessage(chatDockMessages.headerTitle)
      : undefined) ||
    formatMessage(chatDockMessages.untitled);

  return (
    <Box
      key={conversation.key}
      className={`${classes.conversationItem} ${
        isDisabled ? classes.conversationItemDisabled : ''
      }`}
      onClick={() => {
        if (isDisabled) return;
        openConversation({
          conversationId: conversation.conversationId,
          patientId: resolvedPatientId || conversation.patientId,
          title: displayTitle,
          userIds: conversation.userIds,
        });
      }}
      onKeyDown={event => {
        if (isDisabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          openConversation({
            conversationId: conversation.conversationId,
            patientId: resolvedPatientId || conversation.patientId,
            title: displayTitle,
            userIds: conversation.userIds,
          });
        }
      }}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
    >
      <Box className={classes.conversationMeta}>
        <Text size="subbody" weight="bold">
          {displayTitle}
        </Text>
        <Box className={classes.conversationMetaActions}>
          <Text size="caption">
            {renderConversationPreview(conversation.latestMessageTime)}
          </Text>
          <Tooltip title={formatMessage(chatDockMessages.removeConversation)}>
            <IconButton
              aria-label={formatMessage(chatDockMessages.removeConversation)}
              icon={X}
              size={0}
              className={classes.conversationRemove}
              onClick={event => {
                event.stopPropagation();
                handleHideConversation(conversation);
              }}
            />
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export const ChatDockTabTitle = ({
  conversation,
  maskedMode,
  fhirSubjectSearch,
  activeProjectId,
  activeProjectCohortId,
}: {
  conversation: ConversationEntry;
  maskedMode: boolean;
  fhirSubjectSearch: FhirSubjectSearch;
  activeProjectId: string;
  activeProjectCohortId?: string;
}) => {
  const { formatMessage } = useIntl();
  const resolvedPatientId = resolvePatientIdFromConversation(conversation);
  const isPopulationChat = conversation.userIds?.includes(
    POPULATION_AGENT_USERNAME,
  );
  const patientName = usePatientName({
    resolvedPatientId,
    maskedMode,
    fhirSubjectSearch,
    activeProjectId,
    activeProjectCohortId,
  });
  const displayTitle =
    patientName ||
    conversation.title ||
    (isPopulationChat
      ? formatMessage(chatDockMessages.headerTitle)
      : undefined) ||
    formatMessage(chatDockMessages.untitled);

  return (
    <Text size="subbody" weight="bold">
      {displayTitle}
    </Text>
  );
};
